/**
 * Glosario del repositorio de Redes Sociales: qué mide cada métrica o cálculo, cómo se obtiene
 * y de qué archivo sale. Lo muestran los tooltips «ⓘ» (InfoTip) de todas las pestañas.
 */
export const GLOSSARY = {
    Publicaciones: {
        what: 'Número de posts publicados en el periodo.',
        how: 'Cuenta de filas de maestro_publicaciones_mensual (Resumen) o de maestro_publicaciones (Análisis y Publicaciones).',
    },
    Alcance: {
        what: 'Personas únicas que vieron el contenido, según la plataforma.',
        how: 'Suma del alcance de cada post. Como una persona puede ver varios posts, la suma puede contar a alguien más de una vez. LinkedIn no reporta alcance.',
    },
    Impresiones: {
        what: 'Veces que se mostró el contenido (incluye repeticiones de la misma persona).',
        how: 'Suma de impresiones por post. TikTok y YouTube no reportan impresiones.',
    },
    Vistas: {
        what: 'Reproducciones o visualizaciones del contenido (video, reel o post).',
        how: 'Suma de vistas por post, tal como las reporta cada red.',
    },
    'Vistas Orgánicas': {
        what: 'Vistas que no vinieron de pauta pagada (solo Facebook).',
        how: 'Suma de vistas orgánicas por post.',
    },
    Interacciones: {
        what: 'Acciones del público sobre el contenido: reacciones, comentarios, compartidos y guardados (según la red).',
        how: 'Columna «Interacciones» del archivo, que ya viene consolidada por el proceso que actualiza los datos.',
    },
    Clics: { what: 'Clics en enlaces o en el contenido.', how: 'Suma por post. Solo Facebook y LinkedIn lo reportan.' },
    Likes: { what: 'Me gusta del post.', how: 'Suma por post.' },
    Reacciones: { what: 'Reacciones (me gusta, me encanta, etc.).', how: 'Suma por post.' },
    Comentarios: { what: 'Comentarios recibidos.', how: 'Suma por post.' },
    Compartidos: { what: 'Veces que se compartió el post.', how: 'Suma por post.' },
    Guardados: { what: 'Veces que se guardó el post (Instagram y TikTok).', how: 'Suma por post.' },
    Seguimientos: { what: 'Personas que siguieron la cuenta desde el post (Instagram).', how: 'Suma por post.' },
    'Tiempo Total Visto (s)': { what: 'Tiempo total que se vio el video, en segundos (TikTok).', how: 'Suma por post.' },
    'Tiempo Promedio Visto (s)': { what: 'Tiempo promedio de visualización por vista (TikTok).', how: 'Valor del post tal como lo reporta TikTok.' },
    tasa: {
        what: 'Qué proporción del público que vio el contenido interactuó con él.',
        how: 'Interacciones ÷ Alcance, ponderado: se suman las interacciones y el alcance de todos los posts y luego se dividen. Si la red no reporta alcance (LinkedIn) se usa Impresiones.',
    },
    seguidoresFoto: {
        what: 'Seguidores totales del perfil en la fecha de cierre del periodo.',
        how: 'La foto diaria más reciente (≤ fin del periodo) del archivo fotos_seguidores, sumada entre los perfiles filtrados. Sin fotos en el periodo (antes del 4 oct 2026) se usa el cierre del archivo mensual.',
    },
    cambioFoto: {
        what: 'Cuántos seguidores ganó o perdió el perfil entre el inicio y el cierre del periodo.',
        how: 'Foto de cierre − foto de inicio (la última antes del primer día o, si no hay, la primera dentro del periodo). Pasa el cursor por la cifra para ver las fechas usadas.',
    },
    neto: {
        what: 'Movimiento de seguidores reportado por la plataforma.',
        how: 'Suma de (Nuevos − Perdidos) de cada día o mes del periodo. Puede no coincidir con el cambio de la foto porque las plataformas recalculan o depuran cuentas.',
    },
    Nuevos: { what: 'Seguidores nuevos reportados.', how: 'Suma diaria o mensual del archivo de seguidores.' },
    Perdidos: { what: 'Seguidores que dejaron de seguir.', how: 'Suma diaria o mensual del archivo de seguidores.' },
    crecimiento: {
        what: 'Cambio de seguidores totales entre el inicio y el cierre del periodo.',
        how: 'Total al cierre − total al inicio, y en % sobre el inicio. Viene del total diario (Seguidores) o del cierre mensual (Análisis).',
    },
    estimado: {
        what: 'El archivo marcó esos días como «reconstruidos»: la plataforma no dio el total y se estimó.',
        how: 'Se cuentan los días con Fuente Total = reconstruido. Úsalos con cautela.',
    },
    comparacion: {
        what: 'Variación contra el periodo inmediato anterior de la misma duración.',
        how: '(valor actual − valor anterior) ÷ valor anterior. Ej.: si ves ago–oct, se compara contra may–jul.',
    },
    perPost: {
        what: 'Interacciones promedio por publicación.',
        how: 'Interacciones totales ÷ número de posts. Un post viral lo puede inflar; compáralo con la mediana.',
    },
    mediana: {
        what: 'Interacciones del post «típico»: la mitad de los posts está por arriba y la mitad por debajo.',
        how: 'Valor central de la métrica ordenada. No la mueven los posts virales, a diferencia del promedio.',
    },
    variacion: {
        what: 'Qué tan disparejo es el rendimiento entre posts (coeficiente de variación).',
        how: 'Desviación estándar ÷ promedio. 50% = posts bastante parejos; más de 150% = depende de unos pocos posts muy exitosos.',
    },
    participacion: {
        what: 'Parte de todas las interacciones del filtro que aportó este grupo.',
        how: 'Interacciones del grupo ÷ interacciones de todos los grupos visibles.',
    },
    indice: {
        what: 'Puntaje de performance relativo al resto de grupos visibles. 0 = promedio, positivo = mejor que el promedio.',
        how: 'Para tasa de interacción, interacciones por post y crecimiento de seguidores se calcula el z-score [(valor − promedio de los grupos) ÷ desviación estándar] y se promedian los tres. Si omites grupos, se recalcula contra los que quedan.',
    },
    matriz: {
        what: 'Ubica cada perfil por su alcance promedio por post (eje horizontal, escala logarítmica) y su tasa de interacción (eje vertical).',
        how: 'Las líneas punteadas son las medianas de los perfiles visibles y dividen los cuatro cuadrantes. Solo entran perfiles con al menos 2 posts y alcance. Clic en un punto para omitir ese perfil.',
    },
    cuadrantes: {
        what: 'Estrella: mucho alcance y alta interacción. Nicho: poco alcance pero comunidad muy activa. Masivo: llega a mucha gente pero interactúa poco. Rezagado: bajo en ambos.',
        how: 'Comparación de cada perfil contra la mediana de alcance por post y de tasa.',
    },
    distribucion: {
        what: 'Cómo se reparte la métrica entre los posts de cada red.',
        how: 'Q1 = 25% de los posts está por debajo; Q3 = 75%. La caja va de Q1 a Q3 y la raya es la mediana. Atípico = post por encima de Q3 + 1.5 × (Q3 − Q1).',
    },
    desviacion: {
        what: 'Dispersión típica alrededor del promedio.',
        how: 'Desviación estándar muestral de la métrica por post.',
    },
    atipicos: {
        what: 'Posts con resultados excepcionalmente altos (virales) respecto a su red.',
        how: 'Métrica > Q3 + 1.5 × rango intercuartil de su red en el periodo.',
    },
    mejorFranja: {
        what: 'Día y franja de 3 horas en que los posts rinden mejor.',
        how: 'Mediana de la métrica de los posts publicados en cada celda (hora local del archivo). Solo cuentan celdas con 3 posts o más, para no premiar un caso aislado.',
    },
    horarios: {
        what: 'Cuándo publica cada red y cómo rinden esos posts.',
        how: '«Cantidad» cuenta los posts por día de la semana u hora de publicación; «Mediana» muestra el rendimiento típico de los posts de esa hora o día.',
    },
    porTipo: {
        what: 'Qué formatos rinden mejor en cada red.',
        how: 'Mediana y promedio de la métrica por red y tipo de post (columna Tipo del archivo).',
    },
    correlacion: {
        what: 'Qué tanto suben o bajan juntas dos métricas en los posts del filtro.',
        how: 'Coeficiente de Pearson: 1 = siempre suben juntas, 0 = sin relación, −1 = cuando una sube la otra baja. Correlación no implica causa.',
    },
    nube: {
        what: 'Temas y hashtags más usados en los textos de los posts.',
        how: 'Se quitan enlaces, menciones, números y palabras vacías (de, para, que…). «Por interacción» agranda las palabras cuyos posts tienen más interacciones promedio (mínimo 2 posts).',
    },
    cliente: {
        what: 'Agrupa los perfiles de la misma marca en todas sus redes.',
        how: 'Se deduce del nombre del perfil (ej. «Bafar», «Bafar México» y «Bafar (bafar_mexico)» = Bafar) y usa el nombre del cliente del sistema cuando coincide.',
    },
    kpiPropio: {
        what: 'KPI personal creado con «Agrega tu KPI»; solo tú lo ves.',
        how: 'Se suma cada campo de la fórmula sobre los datos filtrados y luego se aplica la fórmula.',
    },
    omitir: {
        what: 'Quita perfiles o clientes de todos los cálculos y gráficas del módulo.',
        how: 'Se recalculan totales, promedios, medianas, índices y comparaciones sin ellos. Se restauran desde la barra «Omitidos».',
    },
};

/** Entrada del glosario por llave o por nombre de métrica. */
export const glossaryFor = (key) => GLOSSARY[key] || null;
